import { forwardRef } from 'react';
import { useTranslation } from 'react-i18next';
import { WifiOff } from 'lucide-react';
import { openUsageModal } from '../../store/ui$';
import { useCreditBalance } from '../../hooks/useCreditBalance';
import { LOW_CREDIT_UI_THRESHOLD } from '../../utils/creditThresholds';
import { AIChatInputRow } from './AIChatInputRow';
import RAGInputFilters from './RAGInputFilters';
import { ICON_SIZE } from '../../design-system';

/**
 * Composer footer for RAG panels: the filter row, connectivity and credit
 * notices, and the input row. Library and deck panels shared all of this by
 * copy before.
 *
 * The filter row is where mode, source and model switching all live, so it is
 * rendered whenever any of them has something to show. `showFilters` is off in
 * keyword mode, where source scoping does not apply, but mode switching still
 * has to be reachable from there.
 */
export const RAGInputArea = forwardRef(function RAGInputArea({
  value,
  onChange,
  onSend,
  onKeyDown,
  disabled,
  placeholder,
  isStreamingActive,
  onStop,
  showFilters = true,
  isOnline = true,
  filtersLeading,
  filtersTrailing,
  compactFilters = false,
  mergeSettings = false,
}, ref) {
  const { t } = useTranslation(['reader', 'common']);
  const { balance } = useCreditBalance();
  const showLowBalanceNudge =
    balance !== null && balance > 0 && balance < LOW_CREDIT_UI_THRESHOLD;

  return (
    <div className="ai-chat-input-area">
      {(showFilters || filtersLeading || filtersTrailing) && (
        <div className={`input-area-filters${compactFilters ? ' input-area-filters--compact' : ''}`}>
          {filtersLeading}
          {showFilters && <RAGInputFilters compact={compactFilters} mergeSettings={mergeSettings} />}
          {filtersTrailing}
        </div>
      )}

      {!isOnline && (
        <div className="ai-chat-offline">
          <WifiOff size={ICON_SIZE.xxs} />
          <span>{t('reader:ragChat.offline')}</span>
        </div>
      )}

      {showLowBalanceNudge && (
        <div className="ai-chat-low-balance-nudge">
          <span>⚡ {balance} {t('common:credits')} {t('reader:aiChat.creditsLeft')}</span>
          <button
            className="ai-chat-low-balance-topup"
            onClick={() => openUsageModal('balance', 'low_balance_nudge')}
          >
            {t('reader:aiChat.topUp')}
          </button>
        </div>
      )}

      <AIChatInputRow
        ref={ref}
        value={value}
        onChange={onChange}
        onSend={onSend}
        onKeyDown={onKeyDown}
        disabled={disabled}
        placeholder={placeholder}
        isStreamingActive={isStreamingActive}
        onStop={onStop}
      />
    </div>
  );
});

export default RAGInputArea;
